export type THttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type THeaders = Record<string, string>;

export type TValidationError = {
  key: string;
  name: string;
  error: string;
};

export type TResponse = {
  result?: any;
  error?: string;
  code?: number;
  validation?: TValidationError;
};

class RestAPI {
  private readonly url: string;
  private token: string | null = null;
  private statusCode: number = 0;
  private instances: Record<string, object> = {};
  private authErrorHandler?: () => void;
  private headersHandler?: (headers: THeaders) => void;
  public validation?: TValidationError;
  public debug: boolean = false;

  constructor(url: string, debug: boolean) {
    this.url = url;
    this.debug = debug;
  }

  public getUrl = (): string => {
    return this.url;
  };

  setAuthErrorHandler = (handler?: () => void) => {
    this.authErrorHandler = handler;
  };

  setHeadersHandler = (handler?: (headers: THeaders) => void) => {
    this.headersHandler = handler;
  };

  setToken = (token: string | null): this => {
    this.token = token;
    return this;
  };

  getToken = (): string | null => {
    return this.token;
  };

  getStatusCode = (): number => {
    return this.statusCode;
  };

  get = <T>(
    endpoint: string,
    payload?: object | FormData,
    fields?: string[]
  ): Promise<T> => {
    return this.request("GET", endpoint, payload, fields);
  };

  post = <T>(
    endpoint: string,
    payload?: object | FormData,
    fields?: string[]
  ): Promise<T> => {
    return this.request("POST", endpoint, payload, fields);
  };

  put = <T>(
    endpoint: string,
    payload?: object | FormData,
    fields?: string[]
  ): Promise<T> => {
    return this.request("PUT", endpoint, payload, fields);
  };

  patch = <T>(
    endpoint: string,
    payload?: object | FormData,
    fields?: string[]
  ): Promise<T> => {
    return this.request("PATCH", endpoint, payload, fields);
  };

  delete = <T>(
    endpoint: string,
    payload?: object | FormData,
    fields?: string[]
  ): Promise<T> => {
    return this.request("DELETE", endpoint, payload, fields);
  };

  private request = <T>(
    method: THttpMethod,
    endpoint: string,
    payload: object | FormData = {},
    fields: string[] = []
  ): Promise<T> => {
    // @ts-ignore
    return new Promise((resolve, reject) => {
      const processReject = (
        error: string,
        code: number,
        validation?: TValidationError
      ) => {
        this.validation = validation;
        if (this.debug) console.error("Error", error, validation);
        if (code === 401 && this.authErrorHandler) this.authErrorHandler();
        else reject(error);
      };

      const options: {
        method: string;
        headers: THeaders;
        body?: FormData | string;
      } = {
        method: method.toUpperCase(),
        headers: {
          accept: "application/json",
        },
      };

      if (payload instanceof FormData) {
        payload.append("fields", fields.join(","));
        options.body = payload;
      } else {
        options.headers["content-type"] = "application/json";
        // @ts-ignore
        payload["fields"] = fields;
        if (payload && method !== "GET") options.body = JSON.stringify(payload);
      }

      if (this.token) {
        options.headers["authorization"] = "Bearer " + this.token;
      }

      this.statusCode = 0;
      this.validation = undefined;

      if (payload && method === "GET") {
        endpoint += "?__payload=" + encodeURIComponent(JSON.stringify(payload));
      }

      if (this.debug)
        console.log(
          "Request",
          method,
          endpoint.split("?")[0],
          JSON.parse(JSON.stringify(payload))
        );

      if (this.headersHandler) {
        this.headersHandler(options.headers);
      }

      fetch(this.url + endpoint, options)
        .then((response) => {
          this.statusCode = response.status;
          response
            .json()
            .then((data: TResponse) => {
              if (data.error)
                processReject(data.error, response.status, data.validation);
              else {
                if (this.debug) console.info("Result", data.result);
                resolve(data.result);
              }
            })
            .catch((e) => processReject(e, -2));
        })
        .catch((e) => processReject(e, -1));
    });
  };

  get Batches(): Batches {
    return (
      (this.instances["Batches"] as Batches) ??
      (this.instances["Batches"] = new Batches(this))
    );
  }

  get Badges(): Badges {
    return (
      (this.instances["Badges"] as Badges) ??
      (this.instances["Badges"] = new Badges(this))
    );
  }

  get Billing(): Billing {
    return (
      (this.instances["Billing"] as Billing) ??
      (this.instances["Billing"] = new Billing(this))
    );
  }

  get VideoEmbeds(): VideoEmbeds {
    return (
      (this.instances["VideoEmbeds"] as VideoEmbeds) ??
      (this.instances["VideoEmbeds"] = new VideoEmbeds(this))
    );
  }

  get Stats(): Stats {
    return (
      (this.instances["Stats"] as Stats) ??
      (this.instances["Stats"] = new Stats(this))
    );
  }

  get Telegram(): Telegram {
    return (
      (this.instances["Telegram"] as Telegram) ??
      (this.instances["Telegram"] = new Telegram(this))
    );
  }

  get Melomania(): Melomania {
    return (
      (this.instances["Melomania"] as Melomania) ??
      (this.instances["Melomania"] = new Melomania(this))
    );
  }

  get Users(): Users {
    return (
      (this.instances["Users"] as Users) ??
      (this.instances["Users"] = new Users(this))
    );
  }

  get VideoContent(): VideoContent {
    return (
      (this.instances["VideoContent"] as VideoContent) ??
      (this.instances["VideoContent"] = new VideoContent(this))
    );
  }

  get Assets(): Assets {
    return (
      (this.instances["Assets"] as Assets) ??
      (this.instances["Assets"] = new Assets(this))
    );
  }

  get Referrals(): Referrals {
    return (
      (this.instances["Referrals"] as Referrals) ??
      (this.instances["Referrals"] = new Referrals(this))
    );
  }

  get ContentPlan(): ContentPlan {
    return (
      (this.instances["ContentPlan"] as ContentPlan) ??
      (this.instances["ContentPlan"] = new ContentPlan(this))
    );
  }

  get Docs(): Docs {
    return (
      (this.instances["Docs"] as Docs) ??
      (this.instances["Docs"] = new Docs(this))
    );
  }

  get ReviewTracks(): ReviewTracks {
    return (
      (this.instances["ReviewTracks"] as ReviewTracks) ??
      (this.instances["ReviewTracks"] = new ReviewTracks(this))
    );
  }

  get Main(): Main {
    return (
      (this.instances["Main"] as Main) ??
      (this.instances["Main"] = new Main(this))
    );
  }

  get Tasks(): Tasks {
    return (
      (this.instances["Tasks"] as Tasks) ??
      (this.instances["Tasks"] = new Tasks(this))
    );
  }

  get Channels(): Channels {
    return (
      (this.instances["Channels"] as Channels) ??
      (this.instances["Channels"] = new Channels(this))
    );
  }

  get ReactorChannels(): ReactorChannels {
    return (
      (this.instances["ReactorChannels"] as ReactorChannels) ??
      (this.instances["ReactorChannels"] = new ReactorChannels(this))
    );
  }

  get Reports(): Reports {
    return (
      (this.instances["Reports"] as Reports) ??
      (this.instances["Reports"] = new Reports(this))
    );
  }

  get AdminStats(): AdminStats {
    return (
      (this.instances["AdminStats"] as AdminStats) ??
      (this.instances["AdminStats"] = new AdminStats(this))
    );
  }

  get Tracks(): Tracks {
    return (
      (this.instances["Tracks"] as Tracks) ??
      (this.instances["Tracks"] = new Tracks(this))
    );
  }

  get Performers(): Performers {
    return (
      (this.instances["Performers"] as Performers) ??
      (this.instances["Performers"] = new Performers(this))
    );
  }

  get Notifications(): Notifications {
    return (
      (this.instances["Notifications"] as Notifications) ??
      (this.instances["Notifications"] = new Notifications(this))
    );
  }

  get Albums(): Albums {
    return (
      (this.instances["Albums"] as Albums) ??
      (this.instances["Albums"] = new Albums(this))
    );
  }

  get Monitoring(): Monitoring {
    return (
      (this.instances["Monitoring"] as Monitoring) ??
      (this.instances["Monitoring"] = new Monitoring(this))
    );
  }

  get Applications(): Applications {
    return (
      (this.instances["Applications"] as Applications) ??
      (this.instances["Applications"] = new Applications(this))
    );
  }

  get Attachments(): Attachments {
    return (
      (this.instances["Attachments"] as Attachments) ??
      (this.instances["Attachments"] = new Attachments(this))
    );
  }

  get Storage(): Storage {
    return (
      (this.instances["Storage"] as Storage) ??
      (this.instances["Storage"] = new Storage(this))
    );
  }

  get Utils(): Utils {
    return (
      (this.instances["Utils"] as Utils) ??
      (this.instances["Utils"] = new Utils(this))
    );
  }

  get Companies(): Companies {
    return (
      (this.instances["Companies"] as Companies) ??
      (this.instances["Companies"] = new Companies(this))
    );
  }

  get Reactrino(): Reactrino {
    return (
      (this.instances["Reactrino"] as Reactrino) ??
      (this.instances["Reactrino"] = new Reactrino(this))
    );
  }

  get Genres(): Genres {
    return (
      (this.instances["Genres"] as Genres) ??
      (this.instances["Genres"] = new Genres(this))
    );
  }

  get Library(): Library {
    return (
      (this.instances["Library"] as Library) ??
      (this.instances["Library"] = new Library(this))
    );
  }
}

export { RestAPI };

export type TDateTime = string;

export type TDateTimeZone = string;

export type TIdentifier = string | number;

export interface IMonitoringChannel {
  id: number;
  channel: IChannel;
  categories?: IMonitoringCategory[];
  user?: IUser | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
}

export interface IChannel {
  id: number;
  extId: string;
  handle: string | null;
  name: string;
  icon: IAsset | null;
  subscribers: number;
  views: number;
  videos: number;
  isAvailable: boolean;
  createdAt?: TDateTime;
  checkedAt?: TDateTime;
  url: string;
}

export interface IReport {
  id: number;
  distributor: EDistributor;
  type: EReportType;
  year: number;
  file: IAsset;
  isProcessed: boolean;
  isPaid: boolean;
  total: number;
  service: number;
  system: number;
  performer: number;
  youtuber: number;
  referral: number;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title: string;
  periodBegin: TDateTime;
  periodEnd: TDateTime;
}

export interface IVideoEmbed {
  id: number;
  tag: string;
  user?: IUser;
  language?: ELanguage;
  comment?: string;
  status?: EReviewStatus;
  rejectReason?: string | null;
  tracks?: ITrack[];
  preview: IAsset | null;
  video?: IAsset | null;
  views?: number;
  videoCount?: number;
  name: string | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
}

export interface IVideoEmbedItem {
  id: number;
  videoEmbed?: IVideoEmbed;
  type: EVideoEmbedItemType;
  track: ITrack | null;
  duration: number;
  video: IAsset | null;
  audio: IAsset | null;
  extra?: Record<string, any>;
  videoRange: [number, number] | null;
  audioRange: [number, number] | null;
  title: string | null;
  text: string | null;
  scheme: EScheme | null;
}

export interface IAsset {
  id: string;
  name: string;
  mime: string;
  size: number;
  isS3?: boolean;
  createdAt?: TDateTime;
  checkedAt?: TDateTime;
  extra?: Record<string, any>;
  url: string;
  duration?: number | null;
  durationString?: string | null;
  resolution?: [] | null;
  resolutionString?: string | null;
}

export interface ITrack {
  id: number;
  tag: string;
  title: string;
  feat: string | null;
  version: string | null;
  authorLyrics?: string | null;
  authorMusic?: string;
  file?: IAsset;
  sample?: IAsset | null;
  performer: IPerformer;
  album?: IAlbum;
  mainGenre?: IGenre | null;
  subGenre?: IGenre | null;
  mood?: EMusicMood;
  lyrics?: string | null;
  isrc?: string | null;
  audioStatus?: EContentIdStatus;
  videoStatus?: EContentIdStatus;
  videoClaim?: IAsset | null;
  language?: ELanguage | null;
  status?: EReviewStatus;
  reviewStatus?: ETrackReviewStatus | null;
  isPublished?: boolean;
  rejectReason?: string | null;
  scheme?: EScheme | null;
  distributor?: EDistributor | null;
  videoEmbed?: IVideoEmbed | null;
  comment?: string | null;
  hash?: string | null;
  duration?: number;
}

export interface IContentPlanVideo {
  id: number;
  channel?: IReactorChannel;
  title: string;
  subtitle: string | null;
  icon: IAsset | null;
  origin: EContentPlanVideoOrigin;
  status: EContentPlanVideoStatus;
  originExtId?: string | null;
  extId?: string | null;
  scheduledAt: TDateTime | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  videoUrl: string | null;
  originVideoUrl: string | null;
}

export interface IVideoContent {
  id: number;
  user?: IUser;
  media: IAsset;
  category: EVideoContentCategory;
  assignors: string[];
  track: string | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  duration: number;
}

export interface ICompany {
  id: number;
  name: string;
  tradingAs: string;
  number?: string;
  registeredIn?: string;
  address?: string;
  director?: string;
  url?: string;
  email?: string;
  bankDetails?: IBankDetails;
}

export interface ITransaction {
  id: number;
  user?: IUser;
  type: ETransactionType;
  balanceBefore: number;
  amount: number;
  balanceAfter: number;
  comment: string | null;
  attachment: IAsset | null;
  createdAt: TDateTime;
  extra?: Record<string, any>;
}

export interface IAlbum {
  id: number;
  title: string;
  cover: IAsset | null;
  performer?: IPerformer;
  tracks?: ITrack[];
  batch?: IBatch | null;
  status?: EReviewStatus;
  rejectReason?: string | null;
  isSingle: boolean;
  tracksCount: number;
  links?: IAlbumLinks;
  upc?: string | null;
  flagReplaceCover?: boolean;
  flagReadyForReview?: boolean;
  releasedAt: TDateTime | null;
}

export interface IPartnerChannel {
  id: number;
  user?: IUser;
  channel?: IChannel;
  status: EPartnerChannelStatus;
  contacts: string;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  isDeleted?: boolean;
  deletedAt?: TDateTime | null;
}

export interface IReactorComment {
  id: number;
  channel?: IReactorChannel;
  title: string;
  performer: string;
  count: number;
  createdAt: TDateTime;
}

export interface IPerformer {
  id: number;
  user?: IUser;
  nationality?: ECountry | null;
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
  stageName: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  passport?: IPassport;
  passportScan?: IAsset | null;
  passportProof?: IAsset | null;
  addressScan?: IAsset | null;
  bankDetails?: IBankDetails;
  bankDetailsProof?: IAsset | null;
  status: EReviewStatus;
  rejectReason?: string | null;
  bio?: string | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  photos?: IAsset[];
  similar?: string | null;
  fullName?: string;
}

export interface IBatch {
  id: number;
  performer?: IPerformer;
  contractBlank?: IAsset | null;
  contractOne?: IAsset | null;
  contractTwo?: IAsset | null;
  albums?: IAlbum[];
  trackCost?: number;
  scheme?: EScheme;
  distributor?: EDistributor | null;
  company?: ICompany | null;
  isPaid?: boolean;
  cost?: number;
  tracksCount?: number;
  createdAt: TDateTime;
  chargeTransaction?: ITransaction | null;
  payoutTransaction?: ITransaction | null;
  contractPeriod?: number;
  attachments?: IAttachment[];
  tag: string;
}

export interface IMonitoringVideo {
  id: number;
  channel?: IMonitoringChannel;
  extId: string;
  title: string;
  preview: IAsset | null;
  status: EReviewStatus;
  isPlanned: boolean;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  url: string;
}

export interface IPerformerApplication {
  id: number;
  name: string;
  email: string;
  contacts: string;
  unpublishedTracks: string | null;
  originalBeats: string | null;
  comment: string | null;
  questions: string | null;
  status: EReviewStatus;
  rejectReason: string | null;
  createdAt: TDateTime;
  updatedAt: TDateTime | null;
  samples: IAsset[];
  source: string | null;
  ip: string | null;
  country: ECountry | null;
  user?: IUser | null;
  service: EService;
  locale: string;
  bio: string | null;
  duplicate: IPerformerApplication | null;
  manager?: IUser | null;
}

export interface IReactorChannel {
  id: number;
  user?: IUser;
  channel: IChannel;
  categories?: IMonitoringCategory[];
  countries?: ECountry[];
}

export interface IReportStat {
  period?: string;
  country?: ECountry;
  retailer?: string;
  distributor: EDistributor;
  track?: ITrack;
  views?: number;
  service?: number;
  performer?: number;
  youtuber?: number;
  referral?: number;
  system?: number;
  total?: number;
}

export interface INotification {
  id: number;
  user?: IUser;
  icon: IAsset | null;
  image: IAsset | null;
  message: string;
  isNew: boolean;
  url: string | null;
  createdAt: TDateTime;
  extra?: Record<string, any>;
}

export interface IVideo {
  id: number;
  extId: string;
  channel?: IChannel | null;
  hasTrack: boolean;
  cover: IAsset | null;
  title: string | null;
  videoEmbed?: IVideoEmbed;
  status: EVideoStatus;
  views: number;
  duration: number;
  createdAt?: TDateTime;
  checkedAt?: TDateTime;
}

export interface ITag {
  id: number;
  title: string;
}

export interface IScreenshot {
  id: number;
  channel: IChannel | null;
  video: IVideo | null;
  asset: IAsset;
  url: string;
  user: IUser;
  createdAt: TDateTime;
}

export interface IAttachment {
  id: number;
  asset: IAsset;
  comment: string | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
}

export interface IUser {
  id: number;
  status: EUserStatus;
  service: EService | null;
  email: string;
  role: EUserRole;
  permissions?: EPermission[];
  password?: string;
  balance?: number;
  ref?: IUser | null;
  refTag?: string;
  source?: string | null;
  createdAt?: TDateTime;
  accessedAt?: TDateTime;
  locale?: string;
  ip?: string | null;
  country?: ECountry | null;
  contacts?: IContacts;
  manager?: IUser | null;
  name: string | null;
  avatar: IAsset | null;
  telegramChatId?: number | null;
  flagYoutuberMonetizationEnabled?: boolean;
  isBotConnected?: boolean;
}

export interface IGenre {
  id: number;
  name: string;
}

export interface IMonitoringCategory {
  id: number;
  name: string;
}

export interface IYoutuberApplication {
  id: number;
  email: string;
  contacts: string;
  views: string;
  links: string[];
  comment: string | null;
  status: EReviewStatus;
  rejectReason: string | null;
  createdAt: TDateTime;
  updatedAt: TDateTime | null;
  source: string | null;
  ip: string | null;
  country: ECountry | null;
  user?: IUser | null;
  ref?: IUser | null;
  service: EService;
  manager?: IUser | null;
}

export interface IDoc {
  id: number;
  parent?: IDoc | null;
  tag: string | null;
  title: string;
  content?: string | null;
  position: number;
  isPublished: boolean;
  parentId?: number | null;
}

export interface ILibraryItem {
  id: number;
  type: ELibraryItemType;
  video: IAsset;
  preview: IAsset | null;
}

export interface IViewStat {
  item?: EViewStatItem;
  itemId: number;
  date: TDateTime;
  views: number;
}

export interface IMonitoringSubscription {
  id: number;
  user?: IUser;
  category: IMonitoringCategory;
}

export interface IHealth {
  status: "ok" | "warning" | "danger" | "unknown";
  diskUsed: number;
  diskAvailable: number;
  diskTotal: number;
  diskUsage: number;
  memoryUsed: number;
  memoryAvailable: number;
  memoryTotal: number;
  memoryUsage: number;
  cpuCount: number;
  cpuUsage: number;
  loadAvg: number[];
}

export interface IAdminBadges {
  reviewPerformerApplications: number;
  reviewYoutubersApplications: number;
  reviewPerformerProfiles: number;
  reviewVideoEmbedRequests: number;
  reviewAlbums: number;
  reviewTracks: number;
  reviewTracksInit: number;
  reviewTracksContentId: number;
  reviewTracksSpotify: number;
  reviewTracksMeta: number;
  unsignedBatches: number;
  unpaidBatches: number;
  albumCovers: number;
  review: number;
  batches: number;
}

export interface ICommonRenameRequest {
  name?: string | null;
}

export interface ICommonRejectRequest {
  reason: string;
}

export interface IInitPartialUploadRequest {
  chunkSize: number;
  fileSize: number;
  fileName: string;
  fileType?: string;
  extra?: boolean;
  resize?: number | null;
  lock?: boolean;
}

export interface IPartialUploadStatus {
  id: string;
  fileName?: string;
  fileType?: string;
  fileSize: number;
  chunkSize: number;
  uploaded: number;
  progress: number;
  asset: IAsset | null;
  request?: IInitPartialUploadRequest;
  isReady: boolean;
}

export interface IUploadRequest {
  upload: { name: string; data: string };
}

export interface IPartialChunkUploadRequest {
  id: string;
  chunk: string;
}

export interface ISignBatchContractRequest {
  documentId: string;
}

export interface IPayoutBatchRequest {
  comment: string;
  attachmentId?: string | null;
}

export interface IGetBatchesRequest {
  preset?: "unsigned" | "unpaid" | "finished" | null;
  performerId?: number | null;
  companyId?: number | null;
  page?: number;
  limit?: number;
}

export interface IAddContentPlanVideoRequest {
  originUrl: string;
  origin?: EContentPlanVideoOrigin;
  scheduledAt: string;
}

export interface ISubmitMonetizationApplicationRequest {
  channel: number;
  views: number;
  contacts: IContacts;
}

export interface IAddReactionChannelRequest {
  url: string;
  countries: ECountry[];
  categories: number[];
}

export interface IUpdateReactionChannelRequest {
  countries?: ECountry[];
  categories?: number[];
}

export interface IGetReactorCommentsResponse {
  pager: IPagedData<IReactorComment>;
  pendingUpdate: boolean;
  refreshEnabled: boolean;
  updatedAt: TDateTime | null;
}

export interface ITrendingItem {
  id: string;
  title: string;
  image: string;
  country: ECountry;
  isPlanned: boolean;
  index?: number;
  url: string;
}

export interface IGetContentPlanRequest {
  dateFrom?: string | null;
  dateTill?: string | null;
  order?: ESortOrder;
}

export interface IUpdateContentPlanVideoRequest {
  status?: EContentPlanVideoStatus;
  videoUrl?: string;
  scheduledAt?: string;
}

export interface IPerformerStatsItem {
  item: ITrack | IAlbum | ECountry | string | null;
  views: number;
  profit: number;
  currency: ECurrency;
}

export interface IYoutuberStats {
  data: IYoutuberStatsItem[];
  views: number | null;
  paidViews: number;
  profit: number;
  cpm: number | null;
  currency: ECurrency;
}

export interface IGetSystemStatsRequest {
  group: "period" | "track" | "embed" | "country";
  country?: ECountry | ECountry[];
  periodBegin?: string | null;
  periodEnd?: string | null;
}

export interface ISystemStatsItem {
  item: IVideoEmbed | ITrack | IAlbum | ECountry | string | null;
  views: number;
  total: number;
  service: number;
  performer: number;
  youtuber: number;
  referral: number;
  system: number;
  currency: ECurrency;
}

export interface IYoutuberStatsItem {
  item: IVideoEmbed | ECountry | string | null;
  views: number | null;
  paidViews: number;
  profit: number;
  cpm: number | null;
  currency: ECurrency;
}

export interface IViewsHistoryItem {
  date: string;
  value: number;
}

export interface IUserViewsHistory {
  balance: number;
  views: number;
  diff: number;
  history: IViewsHistoryItem[];
}

export interface IVideoEmbedViewsHistory {
  views: number;
  diff: number;
  history: IViewsHistoryItem[];
}

export interface IGetYoutuberStatsRequest {
  group: "period" | "embed" | "country";
  country?: ECountry | ECountry[];
  embed?: number | number[];
  periodBegin?: string | null;
  periodEnd?: string | null;
}

export interface IGetPerformerStatsRequest {
  group: "period" | "album" | "track" | "country" | "retailer";
  retailer?: string | string[];
  country?: ECountry | ECountry[];
  album?: number | number[];
  track?: number | number[];
  periodBegin?: string | null;
  periodEnd?: string | null;
}

export interface ISystemStats {
  data: ISystemStatsItem[];
  views: number;
  total: number;
  service: number;
  performer: number;
  youtuber: number;
  referral: number;
  system: number;
  currency: ECurrency;
}

export interface IPerformerStats {
  data: IPerformerStatsItem[];
  views: number;
  profit: number;
  currency: ECurrency;
}

export interface ICreateAttachmentRequest {
  asset: string;
  comment?: string | null;
}

export interface IUpdateAttachmentRequest {
  comment?: string | null;
}

export interface ICommonGetListRequest {
  query?: string;
  order?: ESortOrder;
  page?: number;
  limit?: number;
}

export interface ISetStorageItemRequest {
  key: string;
  value?: any | null;
  ttl?: number | null;
  isGlobal?: boolean;
}

export interface IGetStorageItemRequest {
  key: string;
  isGlobal?: boolean;
}

export interface IDeleteStorageItemRequest {
  key: string;
  isGlobal?: boolean;
}

export interface ICreateAlbumRequest {
  performerId: number;
  title: string;
  coverId?: string | null;
  releasedAt?: string | null;
}

export interface IUpdateAlbumRequest {
  title?: string;
  coverId?: string | null;
  upc?: string | null;
  flagReplaceCover?: boolean | null;
  releasedAt?: string | null;
}

export interface IUpdateAlbumLinksRequest {
  links: IAlbumLinks;
}

export interface ICreateSingleRequest {
  performerId: number;
  single: IAddTrackRequest;
  releasedAt?: string | null;
}

export interface IAlbumLinks {
  fanLink: string | null;
  spotify: string | null;
  appleMusic: string | null;
  tidal: string | null;
  deezer: string | null;
  soundCloud: string | null;
  yandexMusic: string | null;
  amazon: string | null;
  zvuk: string | null;
  array: string[];
}

export interface IGetAlbumsRequest {
  query?: string | null;
  performerId?: number | null;
  status?: EReviewStatus;
  preset?: "normal" | "upload" | "pending" | "review" | "covers";
  withLinks?: boolean;
  excludePerformers?: number[];
  page?: number;
  limit?: number;
}

export interface ICreateMonitoringChannelRequest {
  url: string;
  category: number[] | number;
}

export interface ICreateMonitoringCategoryRequest {
  name: string;
}

export interface IGetMonitoringChannelsRequest {
  query?: string;
  category?: number;
  scope?: "private" | "global";
  page?: number;
  limit?: number;
}

export interface IGetPartnerChannelsRequest {
  query?: string;
  status?: EPartnerChannelStatus;
  page?: number;
  limit?: number;
}

export interface IReferralRecord {
  id: number;
  email: string;
  status: EUserStatus;
  source: string | null;
  channels: number;
  tracks: number;
  views: number;
  createdAt: TDateTime;
  releaseAt: TDateTime;
  country: ECountry | null;
}

export interface IAddPartnerChannelRequest {
  channelId: string;
  contacts: string;
}

export interface IUpdateVideoEmbedRequest {
  videoId?: string | null;
  previewId?: string | null;
}

export interface IBulkOperationRequest {
  operation: "delete" | "cut";
  ids: number[];
}

export interface IVideoEmbedRequest {
  comment: string;
  language: ELanguage;
}

export interface IGetVideoEmbedsRequest {
  status?: EReviewStatus;
  userId?: number;
  page?: number;
  limit?: number;
}

export interface IAddVideoEmbedItemRequest {
  type: EVideoEmbedItemType;
  audioAssetId?: string | null;
  audioRange?: [number, number] | null;
  videoAssetId?: string | null;
  videoRange?: [number, number] | null;
  trackId?: number | null;
  scheme?: EScheme | null;
  title?: string | null;
  text?: string | null;
}

export interface IUpdateVideoEmbedItemRequest {
  audioAssetId?: string | null;
  audioRange?: [number, number] | null;
  videoAssetId?: string | null;
  videoRange?: [number, number] | null;
  trackId?: number | null;
  scheme?: EScheme | null;
  title?: string | null;
  text?: string | null;
}

export interface IGetVideoContentRequest {
  userId?: number;
  query?: string;
  category?: EVideoContentCategory;
  page?: number;
  limit?: number;
}

export interface IAddVideoContentRequest {
  mediaAssetId: string;
  category: EVideoContentCategory;
  assignors: string[];
  track?: string | null;
}

export interface IBulkTracksRequest {
  ids: number[];
}

export interface IGenerateVideoEmbedTextsRequest {
  videoEmbedId: number;
  language: ELanguage;
}

export interface IGenerateVideoEmbedTextsTask {
  videoEmbedId?: number;
  language?: ELanguage;
  advText: string | null;
  reqText: string | null;
  descText: string | null;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface ICustomAiRequest {
  request: string;
}

export interface IExportTracksCoversTask {
  request?: IBulkTracksRequest;
  zip: IAsset | null;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IGenerateReviewTrackVideoTask {
  video: IAsset | null;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IBulkAddVideosTask {
  videoEmbedId: number;
  items: IBulkAddVideosItem[];
  addedCount: number;
  errorsCount: number;
  addingCount: number;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IGenerateTextTask {
  userId?: number;
  subject: string | null;
  body: string | null;
  request?: IGenerateTextRequest;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IProcessReportTask {
  reportId?: number;
  totalRecords: number | null;
  processedRecords: number;
  errors: string[];
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface ICreateReviewAlbumsArchiveTask {
  zip: IAsset | null;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IUpdateTaskRequest {
  email?: string;
}

export interface IExportTracksAudioTask {
  messageClass?: string;
  request?: IBulkTracksRequest;
  zip?: IAsset | null;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IAbstractTask {
  id: string;
  status: ETaskStatus;
  progress: number | null;
  title: string | null;
  description: string | null;
  createdAt: TDateTime;
  updatedAt: TDateTime;
  messageClass?: string;
}

export interface IRenderVideoEmbedTask {
  videoEmbedId?: number;
  preview: IAsset | null;
  result: IAsset | null;
  messageClass?: string;
  isApplyResults?: boolean;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IGenerateTextRequest {
  template: "01" | "02";
  language: ELanguage;
  params: Record<"channelName" | "channelId" | "text" | string, string>;
}

export interface ICustomAiRequestTask {
  request?: string;
  response: string | null;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IMakeLyricsAnalysisTask {
  trackId?: number;
  result: string | null;
  messageClass?: string;
  id?: string;
  status?: ETaskStatus;
  progress?: number | null;
  createdAt?: TDateTime;
  updatedAt?: TDateTime;
  title?: string | null;
  description?: string | null;
}

export interface IChargeUserRequest {
  amount: number;
  type: ETransactionType;
  comment: string;
  attachmentId?: string | null;
}

export interface IGetTransactionsHistoryRequest {
  type?: ETransactionType;
  page?: number;
  limit?: number;
}

export interface IGetLibraryItemsRequest {
  type?: ELibraryItemType;
  page?: number;
  limit?: number;
}

export interface ICreateLibraryItemRequest {
  type: ELibraryItemType;
  assetId: string;
}

export interface IDocPlainNode {
  id: number;
  title: string;
  level: number;
  isPublished: boolean;
}

export interface ICreateDocRequest {
  parentId?: number | null;
  position?: number;
  title: string;
  content?: string | null;
  tag?: string | null;
  isPublished?: boolean;
}

export interface ISortDocsRequest {
  docIds: number[];
}

export interface IGetDocsRequest {
  parentId?: number | null;
  onlyPublished?: boolean;
}

export interface IGetPlainTreeRequest {
  parentId?: number | null;
  excludeIds?: number[];
}

export interface IDocNode {
  id: number | null;
  title: string;
  children: IDocNode[];
}

export interface IDocItem {
  id: number;
  title: string;
}

export interface IUpdateDocRequest {
  parentId?: number | null;
  position?: number;
  title?: string;
  content?: string | null;
  tag?: string | null;
  isPublished?: boolean;
}

export interface ISendAnalyticsRequest {
  url: string;
  data: string;
}

export interface ISettings {
  openAiCredentials: string[];
  googleApiCredentials: string[];
  httpProxies: string[];
  aiLyricsAnalysisPrompt?: string;
}

export interface ICheckVideosRequest {
  extIds: string[];
}

export interface IAddVideoRequest {
  url: string;
  force?: boolean;
}

export interface IBulkAddVideosItem {
  extId: string;
  video: IVideo | null;
  status: "adding" | "success" | "error";
  error: string | null;
}

export interface IBulkAddVideosRequest {
  extIds: string[];
}

export interface IGetVideoRequest {
  query?: string | null;
  status?: EVideoStatus | null;
  page?: number;
  limit?: number;
}

export interface IBulkVideoOperationRequest {
  operation: "cut" | "delete";
  videoIds: number[];
}

export interface IGetTracksRequest {
  query?: string;
  batchId?: number;
  performerId?: number;
  albumId?: number;
  hasIsrc?: boolean;
  hasOwner?: boolean;
  isPublished?: boolean;
  isPaid?: boolean;
  hasVideoEmbed?: boolean;
  language?: ELanguage | ELanguage[];
  distributor?: EDistributor;
  status?: EReviewStatus;
  reviewStatus?: ETrackReviewStatus;
  audioStatus?: EContentIdStatus;
  videoStatus?: EContentIdStatus;
  sort?: "id" | "views";
  order?: ESortOrder;
  page?: number;
  limit?: number;
}

export interface ICreateBatchRequest {
  performerId: number;
  companyId: number;
  scheme: EScheme;
  distributor?: EDistributor | null;
  trackCost: number;
  albums: number[];
  contractPeriod?: number;
}

export interface IAddTrackRequest {
  albumId?: number | null;
  coverId?: string | null;
  fileId: string;
  title: string;
  stageName?: string | null;
  mainGenreId?: number;
  subGenreId: number;
  mood?: EMusicMood;
  lyrics?: string | null;
  authorLyrics?: string | null;
  authorMusic: string;
  feat?: string | null;
  version?: string | null;
  language?: ELanguage | null;
}

export interface ISetReviewStatusRequest {
  reviewStatus: ETrackReviewStatus;
}

export interface IUpdateMonetizationRequest {
  videoStatus?: EContentIdStatus;
  audioStatus?: EContentIdStatus;
  isrc?: string | null;
  videoClaimId?: string | null;
}

export interface IBulkTrackCommentRequest {
  comments: Record<number, string | null>;
}

export interface ITrackInfo {
  track: ITrack;
  mp3: IAsset;
  videoClaim: IAsset | null;
  qrCode: IAsset;
}

export interface IUpdateTrackRequest {
  fileId?: string;
  coverId?: string;
  title?: string;
  mainGenreId?: number;
  subGenreId?: number;
  mood?: EMusicMood;
  lyrics?: string | null;
  authorLyrics?: string | null;
  authorMusic?: string;
  feat?: string | null;
  version?: string | null;
  language?: ELanguage | null;
  isrc?: string | null;
  distributor?: EDistributor | null;
  scheme?: EScheme | null;
  comment?: string | null;
  releasedAt?: string | null;
}

export interface IAddReportRequest {
  distributor?: EDistributor;
  type: EReportType;
  year: number;
  file: string;
}

export interface IBankDetails {
  accountHolder: string;
  bic: string;
  iban: string;
  currency: ECurrency;
  bankName: string;
  bankAddress: string;
  cardNumber: string | null;
  cardHolder: string | null;
  corrName: string | null;
  corrBic: string | null;
  corrIban: string | null;
  payoneerAccount: string | null;
}

export interface IUpdatePerformerRequest {
  nationality?: ECountry;
  lastName?: string;
  firstName?: string;
  middleName?: string | null;
  stageName?: string;
  address?: string;
  passport?: IPassport;
  passportScanId?: string;
  passportProofId?: string;
  addressScanId?: string;
  bankDetails?: IBankDetails;
  bankDetailsProofId?: string;
  phoneNumber?: string;
  bio?: string;
  photoIds?: string[];
  similar?: string | null;
}

export interface ICreatePerformerRequest {
  stageName: string;
}

export interface IGetPerformersListRequest {
  userId?: number | null;
  query?: string | null;
  status?: EReviewStatus | null;
  page?: number;
  limit?: number;
}

export interface IPassport {
  type: string | null;
  series: string | null;
  number: string | null;
  issuedBy: string | null;
  issuedAt: string | null;
}

export interface IUpdateCompanyRequest {
  name: string;
  tradingAs: string;
  number: string;
  registeredIn: string;
  address: string;
  director: string;
  url: string;
  email: string;
  bankDetails: IBankDetails;
}

export interface IPerformerApplicationSubmitRequest {
  name: string;
  email: string;
  contacts: string;
  bio: string;
  questions?: string | null;
  samples: string[];
  unpublishedTracks: string;
  originalBeats: string;
  service?: EService | null;
  source?: string | null;
}

export interface IApproveApplicationRequest {
  email?: string | null;
  password?: string | null;
}

export interface IYoutuberApplicationSubmitRequest {
  email: string;
  contacts: string;
  links: string[];
  views: string;
  service?: EService | null;
  source?: string | null;
  ref?: string | null;
}

export interface IGetApplicationsRequest {
  query?: string;
  status?: EReviewStatus;
  order?: ESortOrder;
  country?: ECountry;
  manager?: number | null;
  page?: number;
  limit?: number;
}

export interface IUpdateApplicationRequest {
  comment?: string | null;
}

export interface IGetUsersListRequest {
  query?: string;
  status?: EUserStatus;
  service?: EService;
  role?: EUserRole;
  manager?: number | null;
  page?: number;
  limit?: number;
}

export interface IFinishPasswordResetRequest {
  token: string;
}

export interface IUpdateUserRequest {
  permissions?: EPermission[];
}

export interface ILoginRequest {
  email: string;
  password: string;
  ttl?: number;
  role?: EUserRole | null;
  service?: EService | null;
}

export interface IUpdateProfileRequest {
  name?: string | null;
  email?: string;
  password?: string;
  avatarId?: string | null;
}

export interface IBeginPasswordResetRequest {
  email: string;
}

export interface IRegisterRequest {
  email: string;
  password: string;
  service?: EService | null;
  source?: string | null;
  ref?: string | null;
}

export interface IContacts {
  telegram: string | null;
  whatsapp: string | null;
  viber: string | null;
  tel: string | null;
}

export enum EContentPlanVideoStatus {
  VideoPending = "pending",
  VideoReady = "ready",
  VideoUploaded = "uploaded",
}

export enum EVideoContentCategory {
  Dance = "dance",
  Reaction = "reaction",
}

export enum EVideoStatus {
  Public = "public",
  Unlisted = "unlisted",
  Unavailable = "unavailable",
}

export enum EPermission {
  Full = "full",
  Tracks = "tracks",
  Review = "review",
  ReviewTracks = "review-tracks",
  Batches = "batches",
  Upload = "upload",
  Accounts = "accounts",
  Library = "library",
  Docs = "docs",
  Stats = "stats",
  Reports = "reports",
  Monitoring = "monitoring",
  Media = "media",
  Albums = "albums",
}

export enum ECurrency {
  AED = "AED",
  AFN = "AFN",
  ALL = "ALL",
  AMD = "AMD",
  ANG = "ANG",
  AOA = "AOA",
  ARS = "ARS",
  AUD = "AUD",
  AWG = "AWG",
  AZN = "AZN",
  BAM = "BAM",
  BBD = "BBD",
  BDT = "BDT",
  BGN = "BGN",
  BHD = "BHD",
  BIF = "BIF",
  BMD = "BMD",
  BND = "BND",
  BOB = "BOB",
  BRL = "BRL",
  BSD = "BSD",
  BTC = "BTC",
  BTN = "BTN",
  BWP = "BWP",
  BYN = "BYN",
  BZD = "BZD",
  CAD = "CAD",
  CDF = "CDF",
  CHF = "CHF",
  CLF = "CLF",
  CLP = "CLP",
  CNH = "CNH",
  CNY = "CNY",
  COP = "COP",
  CRC = "CRC",
  CUC = "CUC",
  CUP = "CUP",
  CVE = "CVE",
  CZK = "CZK",
  DJF = "DJF",
  DKK = "DKK",
  DOP = "DOP",
  DZD = "DZD",
  EGP = "EGP",
  ERN = "ERN",
  ETB = "ETB",
  EUR = "EUR",
  FJD = "FJD",
  FKP = "FKP",
  GBP = "GBP",
  GEL = "GEL",
  GGP = "GGP",
  GHS = "GHS",
  GIP = "GIP",
  GMD = "GMD",
  GNF = "GNF",
  GTQ = "GTQ",
  GYD = "GYD",
  HKD = "HKD",
  HNL = "HNL",
  HRK = "HRK",
  HTG = "HTG",
  HUF = "HUF",
  IDR = "IDR",
  ILS = "ILS",
  IMP = "IMP",
  INR = "INR",
  IQD = "IQD",
  IRR = "IRR",
  ISK = "ISK",
  JEP = "JEP",
  JMD = "JMD",
  JOD = "JOD",
  JPY = "JPY",
  KES = "KES",
  KGS = "KGS",
  KHR = "KHR",
  KMF = "KMF",
  KPW = "KPW",
  KRW = "KRW",
  KWD = "KWD",
  KYD = "KYD",
  KZT = "KZT",
  LAK = "LAK",
  LBP = "LBP",
  LKR = "LKR",
  LRD = "LRD",
  LSL = "LSL",
  LYD = "LYD",
  MAD = "MAD",
  MDL = "MDL",
  MGA = "MGA",
  MKD = "MKD",
  MMK = "MMK",
  MNT = "MNT",
  MOP = "MOP",
  MRU = "MRU",
  MUR = "MUR",
  MVR = "MVR",
  MWK = "MWK",
  MXN = "MXN",
  MYR = "MYR",
  MZN = "MZN",
  NAD = "NAD",
  NGN = "NGN",
  NIO = "NIO",
  NOK = "NOK",
  NPR = "NPR",
  NZD = "NZD",
  OMR = "OMR",
  PAB = "PAB",
  PEN = "PEN",
  PGK = "PGK",
  PHP = "PHP",
  PKR = "PKR",
  PLN = "PLN",
  PYG = "PYG",
  QAR = "QAR",
  RON = "RON",
  RSD = "RSD",
  RUB = "RUB",
  RWF = "RWF",
  SAR = "SAR",
  SBD = "SBD",
  SCR = "SCR",
  SDG = "SDG",
  SEK = "SEK",
  SGD = "SGD",
  SHP = "SHP",
  SLL = "SLL",
  SOS = "SOS",
  SRD = "SRD",
  SSP = "SSP",
  STD = "STD",
  STN = "STN",
  SVC = "SVC",
  SYP = "SYP",
  SZL = "SZL",
  THB = "THB",
  TJS = "TJS",
  TMT = "TMT",
  TND = "TND",
  TOP = "TOP",
  TRY = "TRY",
  TTD = "TTD",
  TWD = "TWD",
  TZS = "TZS",
  UAH = "UAH",
  UGX = "UGX",
  USD = "USD",
  UYU = "UYU",
  UZS = "UZS",
  VES = "VES",
  VND = "VND",
  VUV = "VUV",
  WST = "WST",
  XAF = "XAF",
  XAG = "XAG",
  XAU = "XAU",
  XCD = "XCD",
  XDR = "XDR",
  XOF = "XOF",
  XPD = "XPD",
  XPF = "XPF",
  XPT = "XPT",
  YER = "YER",
  ZAR = "ZAR",
  ZMW = "ZMW",
  ZWL = "ZWL",
}

export enum EMusicMood {
  Chill = "chill",
  Uplifting = "uplifting",
  Sad = "sad",
  Mysterious = "mysterious",
  Angry = "angry",
  Creepy = "creepy",
  Melancholic = "melancholic",
  Dreamy = "dreamy",
  Dark = "dark",
  Intense = "intense",
  Upbeat = "upbeat",
  Relaxing = "relaxing",
  Romantic = "romantic",
  Calm = "calm",
  Scary = "scary",
  Happy = "happy",
  Peaceful = "peaceful",
  Energetic = "energetic",
  Epic = "epic",
  Other = "other",
}

export enum ETaskStatus {
  Queued = "queued",
  Processing = "processing",
  Finished = "finished",
  Failed = "failed",
}

export enum EDistributor {
  FreshTunes = "freshtunes",
  Believe = "believe",
  Orchard = "orchard",
  Yoola = "yoola",
  TuneCore = "tunecore",
  Distrokid = "distrokid",
}

export enum ECountry {
  AU = "au",
  AT = "at",
  AZ = "az",
  AX = "ax",
  AL = "al",
  DZ = "dz",
  VI = "vi",
  AS = "as",
  AI = "ai",
  AO = "ao",
  AD = "ad",
  AQ = "aq",
  AG = "ag",
  AR = "ar",
  AM = "am",
  AW = "aw",
  AF = "af",
  BS = "bs",
  BD = "bd",
  BB = "bb",
  BH = "bh",
  BZ = "bz",
  BY = "by",
  BE = "be",
  BJ = "bj",
  BM = "bm",
  BG = "bg",
  BO = "bo",
  BQ = "bq",
  BA = "ba",
  BW = "bw",
  BR = "br",
  IO = "io",
  VG = "vg",
  BN = "bn",
  BF = "bf",
  BI = "bi",
  BT = "bt",
  VU = "vu",
  VA = "va",
  GB = "gb",
  HU = "hu",
  VE = "ve",
  UM = "um",
  TL = "tl",
  VN = "vn",
  GA = "ga",
  HT = "ht",
  GY = "gy",
  GM = "gm",
  GH = "gh",
  GP = "gp",
  GT = "gt",
  GF = "gf",
  GN = "gn",
  GW = "gw",
  DE = "de",
  GG = "gg",
  GI = "gi",
  HN = "hn",
  HK = "hk",
  GD = "gd",
  GL = "gl",
  GR = "gr",
  GE = "ge",
  GU = "gu",
  DK = "dk",
  JE = "je",
  DJ = "dj",
  DM = "dm",
  DO = "do",
  CD = "cd",
  EU = "eu",
  EG = "eg",
  ZM = "zm",
  EH = "eh",
  ZW = "zw",
  IL = "il",
  IN = "in",
  ID = "id",
  JO = "jo",
  IQ = "iq",
  IR = "ir",
  IE = "ie",
  IS = "is",
  ES = "es",
  IT = "it",
  YE = "ye",
  CV = "cv",
  KZ = "kz",
  KY = "ky",
  KH = "kh",
  CM = "cm",
  CA = "ca",
  QA = "qa",
  KE = "ke",
  CY = "cy",
  KG = "kg",
  KI = "ki",
  TW = "tw",
  KP = "kp",
  CN = "cn",
  CC = "cc",
  CO = "co",
  KM = "km",
  CR = "cr",
  CI = "ci",
  CU = "cu",
  KW = "kw",
  CW = "cw",
  LA = "la",
  LV = "lv",
  LS = "ls",
  LR = "lr",
  LB = "lb",
  LY = "ly",
  LT = "lt",
  LI = "li",
  LU = "lu",
  MU = "mu",
  MR = "mr",
  MG = "mg",
  YT = "yt",
  MO = "mo",
  MK = "mk",
  MW = "mw",
  MY = "my",
  ML = "ml",
  MV = "mv",
  MT = "mt",
  MA = "ma",
  MQ = "mq",
  MH = "mh",
  MX = "mx",
  FM = "fm",
  MZ = "mz",
  MD = "md",
  MC = "mc",
  MN = "mn",
  MS = "ms",
  MM = "mm",
  NA = "na",
  NR = "nr",
  NP = "np",
  NE = "ne",
  NG = "ng",
  NL = "nl",
  NI = "ni",
  NU = "nu",
  NZ = "nz",
  NC = "nc",
  NO = "no",
  AE = "ae",
  OM = "om",
  BV = "bv",
  IM = "im",
  CK = "ck",
  NF = "nf",
  CX = "cx",
  PN = "pn",
  SH = "sh",
  PK = "pk",
  PW = "pw",
  PS = "ps",
  PA = "pa",
  PG = "pg",
  PY = "py",
  PE = "pe",
  PL = "pl",
  PT = "pt",
  PR = "pr",
  CG = "cg",
  KR = "kr",
  RE = "re",
  RU = "ru",
  RW = "rw",
  RO = "ro",
  SV = "sv",
  WS = "ws",
  SM = "sm",
  ST = "st",
  SA = "sa",
  SZ = "sz",
  MP = "mp",
  SC = "sc",
  BL = "bl",
  MF = "mf",
  PM = "pm",
  SN = "sn",
  VC = "vc",
  KN = "kn",
  LC = "lc",
  RS = "rs",
  SG = "sg",
  SX = "sx",
  SY = "sy",
  SK = "sk",
  SI = "si",
  SB = "sb",
  SO = "so",
  SD = "sd",
  SR = "sr",
  US = "us",
  SL = "sl",
  TJ = "tj",
  TH = "th",
  TZ = "tz",
  TC = "tc",
  TG = "tg",
  TK = "tk",
  TO = "to",
  TT = "tt",
  TV = "tv",
  TN = "tn",
  TM = "tm",
  TR = "tr",
  UG = "ug",
  UZ = "uz",
  UA = "ua",
  WF = "wf",
  UY = "uy",
  FO = "fo",
  FJ = "fj",
  PH = "ph",
  FI = "fi",
  FK = "fk",
  FR = "fr",
  PF = "pf",
  TF = "tf",
  HM = "hm",
  HR = "hr",
  CF = "cf",
  TD = "td",
  ME = "me",
  CZ = "cz",
  CL = "cl",
  CH = "ch",
  SE = "se",
  SJ = "sj",
  LK = "lk",
  EC = "ec",
  GQ = "gq",
  ER = "er",
  EE = "ee",
  ET = "et",
  ZA = "za",
  GS = "gs",
  SS = "ss",
  JM = "jm",
  JP = "jp",
}

export enum EScheme {
  White = "white",
  Gray = "gray",
}

export enum EService {
  Zoundo = "zoundo",
  Tubyx = "tubyx",
  TubePays = "tubepays",
  Axtune = "axtune",
  Echotune = "echotune",
  Reactrino = "reactrino",
  Arpeggy = "arpeggy",
}

export enum EUserRole {
  Service = "service",
  Admin = "admin",
  Performer = "performer",
  Youtuber = "youtuber",
  Partner = "partner",
}

export enum ETransactionType {
  Payin = "payin",
  Payout = "payout",
  Referral = "referral",
  Spending = "spending",
  Selling = "selling",
  Promo = "promo",
  Other = "other",
}

export enum EContentPlanVideoOrigin {
  Trending = "trending",
  NewVideos = "new-videos",
  Comments = "comments",
  Other = "other",
}

export enum ETrackReviewStatus {
  Init = 0,
  ContentID = 1,
  Spotify = 2,
  Meta = 3,
}

export enum ELanguage {
  Abkhazian = "ab",
  Afar = "aa",
  Afrikaans = "af",
  Akan = "ak",
  Albanian = "sq",
  Amharic = "am",
  Arabic = "ar",
  Aragonese = "an",
  Armenian = "hy",
  Assamese = "as",
  Avaric = "av",
  Avestan = "ae",
  Aymara = "ay",
  Azerbaijani = "az",
  Bambara = "bm",
  Bashkir = "ba",
  Basque = "eu",
  Belarusian = "be",
  Bengali = "bn",
  Bislama = "bi",
  Bosnian = "bs",
  Breton = "br",
  Bulgarian = "bg",
  Burmese = "my",
  Catalan = "ca",
  Chamorro = "ch",
  Chechen = "ce",
  Chichewa = "ny",
  Chinese = "zh",
  ChurchSlavonic = "cu",
  Chuvash = "cv",
  Cornish = "kw",
  Corsican = "co",
  Cree = "cr",
  Croatian = "hr",
  Czech = "cs",
  Danish = "da",
  Divehi = "dv",
  Dutch = "nl",
  Dzongkha = "dz",
  English = "en",
  Esperanto = "eo",
  Estonian = "et",
  Ewe = "ee",
  Faroese = "fo",
  Fijian = "fj",
  Finnish = "fi",
  French = "fr",
  WesternFrisian = "fy",
  Fulah = "ff",
  Gaelic = "gd",
  Galician = "gl",
  Ganda = "lg",
  Georgian = "ka",
  German = "de",
  Greek = "el",
  Kalaallisut = "kl",
  Guarani = "gn",
  Gujarati = "gu",
  Haitian = "ht",
  Hausa = "ha",
  Hebrew = "he",
  Herero = "hz",
  Hindi = "hi",
  HiriMotu = "ho",
  Hungarian = "hu",
  Icelandic = "is",
  Ido = "io",
  Igbo = "ig",
  Indonesian = "id",
  Interlingua = "ia",
  Interlingue = "ie",
  Inuktitut = "iu",
  Inupiaq = "ik",
  Irish = "ga",
  Italian = "it",
  Japanese = "ja",
  Javanese = "jv",
  Kannada = "kn",
  Kanuri = "kr",
  Kashmiri = "ks",
  Kazakh = "kk",
  CentralKhmer = "km",
  Kikuyu = "ki",
  Kinyarwanda = "rw",
  Kirghiz = "ky",
  Komi = "kv",
  Kongo = "kg",
  Korean = "ko",
  Kuanyama = "kj",
  Kurdish = "ku",
  Lao = "lo",
  Latin = "la",
  Latvian = "lv",
  Limburgan = "li",
  Lingala = "ln",
  Lithuanian = "lt",
  LubaKatanga = "lu",
  Luxembourgish = "lb",
  Macedonian = "mk",
  Malagasy = "mg",
  Malay = "ms",
  Malayalam = "ml",
  Maltese = "mt",
  Manx = "gv",
  Maori = "mi",
  Marathi = "mr",
  Marshallese = "mh",
  Mongolian = "mn",
  Nauru = "na",
  Navajo = "nv",
  NorthNdebele = "nd",
  SouthNdebele = "nr",
  Ndonga = "ng",
  Nepali = "ne",
  Norwegian = "no",
  NorwegianBokmal = "nb",
  NorwegianNynorsk = "nn",
  SichuanYi = "ii",
  Occitan = "oc",
  Ojibwa = "oj",
  Oriya = "or",
  Oromo = "om",
  Ossetian = "os",
  Pali = "pi",
  Pashto = "ps",
  Persian = "fa",
  Polish = "pl",
  Portuguese = "pt",
  Punjabi = "pa",
  Quechua = "qu",
  Romanian = "ro",
  Romansh = "rm",
  Rundi = "rn",
  Russian = "ru",
  NorthernSami = "se",
  Samoan = "sm",
  Sango = "sg",
  Sanskrit = "sa",
  Sardinian = "sc",
  Serbian = "sr",
  Shona = "sn",
  Sindhi = "sd",
  Sinhala = "si",
  Slovak = "sk",
  Slovenian = "sl",
  Somali = "so",
  SouthernSotho = "st",
  Spanish = "es",
  Sundanese = "su",
  Swahili = "sw",
  Swati = "ss",
  Swedish = "sv",
  Tagalog = "tl",
  Tahitian = "ty",
  Tajik = "tg",
  Tamil = "ta",
  Tatar = "tt",
  Telugu = "te",
  Thai = "th",
  Tibetan = "bo",
  Tigrinya = "ti",
  Tonga = "to",
  Tsonga = "ts",
  Tswana = "tn",
  Turkish = "tr",
  Turkmen = "tk",
  Twi = "tw",
  Uighur = "ug",
  Ukrainian = "uk",
  Urdu = "ur",
  Uzbek = "uz",
  Venda = "ve",
  Vietnamese = "vi",
  Volapuk = "vo",
  Walloon = "wa",
  Welsh = "cy",
  Wolof = "wo",
  Xhosa = "xh",
  Yiddish = "yi",
  Yoruba = "yo",
  Zhuang = "za",
  Zulu = "zu",
}

export enum EVideoEmbedItemType {
  Video = "video",
  VideoQR = "video-qr",
  Track = "track",
  Text = "text",
}

export enum EContentIdStatus {
  None = "none",
  Pending = "pending",
  Active = "active",
  Excluded = "excluded",
}

export enum EViewStatItem {
  Video = "video",
  VideoEmbed = "ve",
  ChannelViews = "ch_views",
  ChannelVideos = "ch_videos",
  ChannelSubscribers = "ch_subs",
}

export enum EReportType {
  Q1 = "Q1",
  Q2 = "Q2",
  Q3 = "Q3",
  Q4 = "Q4",
  January = "M01",
  February = "M02",
  March = "M03",
  April = "M04",
  May = "M05",
  June = "M06",
  July = "M07",
  August = "M08",
  September = "M09",
  October = "M10",
  November = "M11",
  December = "M12",
}

export enum EPartnerChannelStatus {
  Message1 = "msg1",
  Reply = "reply",
  Message2 = "msg2",
  Registration = "reg",
}

export enum ELibraryItemType {
  School = "school",
  Contest = "contest",
  Transition = "transition",
}

export enum EUserStatus {
  Active = "active",
  Block = "block",
}

export enum EReviewStatus {
  Draft = "draft",
  Review = "review",
  Approve = "approve",
  Reject = "reject",
}

export enum ESortOrder {
  ASC = "ASC",
  DESC = "DESC",
}

export interface IPagedData<T> {
  page: number;
  limit: number;
  count: number | null;
  pages: number | null;
  data: T[];
}

export enum EFieldGroup {
  MonitoringCategories = "monitoring:categories",
  MonitoringUser = "monitoring:user",
  ChannelDate = "channel:date",
  VideoEmbedUser = "video-embed:user",
  VideoEmbedLanguage = "video-embed:language",
  VideoEmbedComment = "video-embed:comment",
  VideoEmbedStatus = "video-embed:status",
  VideoEmbedTracks = "video-embed:tracks",
  VideoEmbedVideo = "video-embed:video",
  VideoEmbedStats = "video-embed:stats",
  AssetDuration = "asset:duration",
  AssetFull = "asset:full",
  AssetResolution = "asset:resolution",
  AssetS3 = "asset:s3",
  TrackDuration = "track:duration",
  TrackEdit = "track:edit",
  TrackFile = "track:file",
  TrackSample = "track:sample",
  TrackPerformer = "track:performer",
  TrackAlbum = "track:album",
  TrackGenre = "track:genre",
  TrackMood = "track:mood",
  TrackLyrics = "track:lyrics",
  TrackIsrc = "track:isrc",
  TrackStatus = "track:status",
  TrackVideoClaim = "track:video-claim",
  TrackLanguage = "track:language",
  TrackScheme = "track:scheme",
  TrackDistributor = "track:distributor",
  TrackComment = "track:comment",
  ContentPlanChannel = "content-plan:channel",
  VideoContentUser = "video-content:user",
  CompanyDetails = "company:details",
  TransactionUser = "transaction:user",
  TransactionFull = "transaction:full",
  AlbumPerformer = "album:performer",
  AlbumTracks = "album:tracks",
  AlbumBatch = "album:batch",
  AlbumStatus = "album:status",
  AlbumLinks = "album:links",
  AlbumUpc = "album:upc",
  AlbumFlags = "album:flags",
  PartnerChannelUser = "partner-channel:user",
  PartnerChannelChannel = "partner-channel:channel",
  PerformerUser = "performer:user",
  PerformerEdit = "performer:edit",
  PerformerStatus = "performer:status",
  PerformerBio = "performer:bio",
  PerformerPhotos = "performer:photos",
  PerformerSimilar = "performer:similar",
  BatchPerformer = "batch:performer",
  BatchFull = "batch:full",
  BatchContract = "batch:contract",
  BatchAlbums = "batch:albums",
  BatchCost = "batch:cost",
  BatchScheme = "batch:scheme",
  BatchDistributor = "batch:distributor",
  BatchCompany = "batch:company",
  BatchTracksCount = "batch:tracks-count",
  BatchTransaction = "batch:transaction",
  BatchContractPeriod = "batch:contract-period",
  BatchAttachments = "batch:attachments",
  MonitoringChannel = "monitoring:channel",
  ApplicationUser = "application:user",
  ApplicationManager = "application:manager",
  ReactorChannelUser = "reactor-channel:user",
  ReactorChannelFull = "reactor-channel:full",
  ReactorChannelCategories = "reactor-channel:categories",
  ReactorChannelCountries = "reactor-channel:countries",
  VideoChannel = "video:channel",
  VideoVideoEmbed = "video:video-embed",
  UserTelegram = "user:telegram",
  UserPermissions = "user:permissions",
  UserBalance = "user:balance",
  UserRef = "user:ref",
  UserSource = "user:source",
  UserDate = "user:date",
  UserLocale = "user:locale",
  UserIp = "user:ip",
  UserCountry = "user:country",
  UserContacts = "user:contacts",
  UserManager = "user:manager",
  UserFlags = "user:flags",
  UserYoutuberFlags = "user:youtuber-flags",
  ApplicationRef = "application:ref",
  DocParentId = "doc:parent-id",
  DocParent = "doc:parent",
  DocContent = "doc:content",
}

class Batches {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getBatchesList = (
    request: IGetBatchesRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IBatch>> => this.api.get(`/batches`, request, fields);

  getBatch = (batch: TIdentifier, fields?: EFieldGroup[]): Promise<IBatch> =>
    this.api.get(`/batches/${batch}`, {}, fields);

  signOneBatch = (
    batch: TIdentifier,
    request: ISignBatchContractRequest,
    fields?: EFieldGroup[]
  ): Promise<IBatch> =>
    this.api.post(`/batches/${batch}/sign/one`, request, fields);

  signTwoBatch = (
    batch: TIdentifier,
    request: ISignBatchContractRequest,
    fields?: EFieldGroup[]
  ): Promise<IBatch> =>
    this.api.post(`/batches/${batch}/sign/two`, request, fields);

  rejectOneBatch = (
    batch: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IBatch> =>
    this.api.delete(`/batches/${batch}/reject/one`, {}, fields);

  chargeBatch = (batch: TIdentifier, fields?: EFieldGroup[]): Promise<IBatch> =>
    this.api.post(`/batches/${batch}/charge`, {}, fields);

  payoutBatch = (
    batch: TIdentifier,
    request: IPayoutBatchRequest,
    fields?: EFieldGroup[]
  ): Promise<IBatch> =>
    this.api.post(`/batches/${batch}/payout`, request, fields);

  deleteBatch = (
    batch: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.delete(`/batches/${batch}`, {}, fields);

  getReadyPerformers = (fields?: EFieldGroup[]): Promise<IPerformer[]> =>
    this.api.get(`/batches/performers/ready`, {}, fields);

  getApprovedAlbums = (
    performer: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IAlbum[]> =>
    this.api.get(`/batches/performers/${performer}/ready-albums`, {}, fields);

  createBatch = (
    request: ICreateBatchRequest,
    fields?: EFieldGroup[]
  ): Promise<IBatch> => this.api.post(`/batches`, request, fields);

  generateContracts = (
    batch: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IBatch> =>
    this.api.post(`/batches/${batch}/contracts`, {}, fields);

  addAttachment = (
    batch: TIdentifier,
    request: ICreateAttachmentRequest,
    fields?: EFieldGroup[]
  ): Promise<IAttachment> =>
    this.api.post(`/batches/${batch}/attachments`, request, fields);

  deleteAttachment = (
    batch: TIdentifier,
    att: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/batches/${batch}/attachments/${att}`, {}, fields);
}

class Badges {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getAdminBadges = (fields?: EFieldGroup[]): Promise<IAdminBadges> =>
    this.api.get(`/badges/admin`, {}, fields);
}

class Billing {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getTrasnactionsHistory = (
    request: IGetTransactionsHistoryRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<ITransaction>> =>
    this.api.get(`/billing/transactions`, request, fields);

  chargeUser = (
    user: TIdentifier,
    request: IChargeUserRequest,
    fields?: EFieldGroup[]
  ): Promise<ITransaction> =>
    this.api.post(`/billing/users/${user}/charge`, request, fields);
}

class VideoEmbeds {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getList = (
    request: IGetVideoEmbedsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IVideoEmbed>> =>
    this.api.get(`/video-embed`, request, fields);

  bulkOperation = (
    request: IBulkOperationRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.post(`/video-embed/bulk`, request, fields);

  requestVideoEmbed = (
    request: IVideoEmbedRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> => this.api.post(`/video-embed`, request, fields);

  approveVideoEmbed = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> =>
    this.api.patch(`/video-embed/${ve}/approve`, {}, fields);

  rejectVideoEmbed = (
    ve: TIdentifier,
    request: ICommonRejectRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> =>
    this.api.patch(`/video-embed/${ve}/reject`, request, fields);

  getVideoEmbed = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> => this.api.get(`/video-embed/${ve}`, {}, fields);

  updateVideoEmbed = (
    ve: TIdentifier,
    request: IUpdateVideoEmbedRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> =>
    this.api.patch(`/video-embed/${ve}`, request, fields);

  renameVideoEmbed = (
    ve: TIdentifier,
    request: ICommonRenameRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> =>
    this.api.post(`/video-embed/${ve}/name`, request, fields);

  addTrackToVideoEmbed = (
    ve: TIdentifier,
    track: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> =>
    this.api.post(`/video-embed/${ve}/tracks/${track}`, {}, fields);

  deleteTrackFromVideoEmbed = (
    ve: TIdentifier,
    track: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> =>
    this.api.delete(`/video-embed/${ve}/tracks/${track}`, {}, fields);

  getVideoEmbedItems = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbedItem[]> =>
    this.api.get(`/video-embed/${ve}/items`, {}, fields);

  addVideoEmbedItem = (
    ve: TIdentifier,
    request: IAddVideoEmbedItemRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbedItem> =>
    this.api.post(`/video-embed/${ve}/items`, request, fields);

  getYoutubeVideos = (
    ve: TIdentifier,
    request: IGetVideoRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IVideo>> =>
    this.api.get(`/video-embed/${ve}/videos`, request, fields);

  addYoutubeVideo = (
    ve: TIdentifier,
    request: IAddVideoRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideo> =>
    this.api.post(`/video-embed/${ve}/videos`, request, fields);

  bulkAddYoutubeVideo = (
    ve: TIdentifier,
    request: IBulkAddVideosRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideo> =>
    this.api.post(`/video-embed/${ve}/videos/bulk`, request, fields);

  resetVideoEmbed = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> =>
    this.api.post(`/video-embed/${ve}/reset`, {}, fields);

  getViews = (
    ve: TIdentifier,
    days: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IViewStat[]> =>
    this.api.get(`/video-embed/${ve}/views/${days}`, {}, fields);

  generateVideoEmbedItems = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.post(`/video-embed/${ve}/generate-items`, {}, fields);

  deleteYoutubeVideo = (
    video: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/video-embed/videos/${video}`, {}, fields);

  cutYoutubeVideo = (
    video: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.post(`/video-embed/videos/${video}/cut`, {}, fields);

  updateVideoEmbedItem = (
    item: TIdentifier,
    request: IUpdateVideoEmbedItemRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbedItem> =>
    this.api.patch(`/video-embed/items/${item}`, request, fields);

  deleteVideoEmbedItem = (
    item: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/video-embed/items/${item}`, {}, fields);

  copyVideoEmbedItem = (
    item: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbedItem> =>
    this.api.post(`/video-embed/items/${item}/copy`, {}, fields);

  deleteVideoEmbed = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbed> => this.api.delete(`/video-embed/${ve}`, {}, fields);

  check = (
    request: ICheckVideosRequest,
    fields?: EFieldGroup[]
  ): Promise<string[]> => this.api.post(`/video-embed/check`, request, fields);
}

class Stats {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getUserViewsHistory = (fields?: EFieldGroup[]): Promise<IUserViewsHistory> =>
    this.api.get(`/stats/user-views-history`, {}, fields);

  getVideoEmbedViewsHistory = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IVideoEmbedViewsHistory> =>
    this.api.get(`/stats/video-embed/${ve}`, {}, fields);

  getPerformerStats = (
    request: IGetPerformerStatsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPerformerStats> =>
    this.api.get(`/stats/performer`, request, fields);

  getYoutuberStats = (
    request: IGetYoutuberStatsRequest,
    fields?: EFieldGroup[]
  ): Promise<IYoutuberStats> =>
    this.api.get(`/stats/youtuber`, request, fields);

  getSystemStats = (
    request: IGetSystemStatsRequest,
    fields?: EFieldGroup[]
  ): Promise<ISystemStats> => this.api.get(`/stats/system`, request, fields);

  getRetailers = (fields?: EFieldGroup[]): Promise<unknown> =>
    this.api.get(`/stats/retailers`, {}, fields);
}

class Telegram {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getNotificationsBotLink = (fields?: EFieldGroup[]): Promise<string | null> =>
    this.api.get(`/telegram/bot`, {}, fields);
}

class Melomania {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getTrack = (tag: TIdentifier, fields?: EFieldGroup[]): Promise<ITrack[]> =>
    this.api.get(`/melomania/tag/${tag}`, {}, fields);
}

class Users {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getUsersList = (
    request: IGetUsersListRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IUser>> => this.api.get(`/users`, request, fields);

  loginAs = (
    user: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<{ token: string; user: IUser }> =>
    this.api.post(`/users/${user}/auth`, {}, fields);

  login = (
    request: ILoginRequest,
    fields?: EFieldGroup[]
  ): Promise<{ token: string; user: IUser }> =>
    this.api.post(`/users/login`, request, fields);

  register = (
    request: IRegisterRequest,
    fields?: EFieldGroup[]
  ): Promise<{ token: string; user: IUser }> =>
    this.api.post(`/users/register`, request, fields);

  beginPasswordReset = (
    request: IBeginPasswordResetRequest,
    fields?: EFieldGroup[]
  ): Promise<true> => this.api.post(`/users/password`, request, fields);

  finishPasswordReset = (
    request: IFinishPasswordResetRequest,
    fields?: EFieldGroup[]
  ): Promise<true> => this.api.post(`/users/password/`, request, fields);

  getMe = (fields?: EFieldGroup[]): Promise<IUser> =>
    this.api.get(`/users/me`, {}, fields);

  updateProfile = (
    request: IUpdateProfileRequest,
    fields?: EFieldGroup[]
  ): Promise<IUser> => this.api.post(`/users/profile`, request, fields);

  getUserById = (user: TIdentifier, fields?: EFieldGroup[]): Promise<IUser> =>
    this.api.get(`/users/${user}`, {}, fields);

  updateUser = (
    user: TIdentifier,
    request: IUpdateUserRequest,
    fields?: EFieldGroup[]
  ): Promise<IUser> => this.api.patch(`/users/${user}`, request, fields);
}

class VideoContent {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getList = (
    request: IGetVideoContentRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IVideoContent>> =>
    this.api.get(`/video-content`, request, fields);

  getAssignors = (fields?: EFieldGroup[]): Promise<string[]> =>
    this.api.get(`/video-content/assignors`, {}, fields);

  addItem = (
    request: IAddVideoContentRequest,
    fields?: EFieldGroup[]
  ): Promise<IVideoContent> => this.api.post(`/video-content`, request, fields);

  getItem = (vc: TIdentifier, fields?: EFieldGroup[]): Promise<IVideoContent> =>
    this.api.get(`/video-content/${vc}`, {}, fields);

  deleteItem = (vc: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.delete(`/video-content/${vc}`, {}, fields);
}

class Assets {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getAsset = (asset: TIdentifier, fields?: EFieldGroup[]): Promise<IAsset> =>
    this.api.get(`/assets/${asset}`, {}, fields);

  uploadJson = (
    request: IUploadRequest,
    fields?: EFieldGroup[]
  ): Promise<IAsset> => this.api.post(`/assets/upload/base64`, request, fields);

  uploadForm = (form: FormData): Promise<IAsset> =>
    this.api.post(`/assets/upload/form`, form);

  beginPartial = (
    request: IInitPartialUploadRequest,
    fields?: EFieldGroup[]
  ): Promise<IPartialUploadStatus> =>
    this.api.post(`/assets/upload/partial`, request, fields);

  chunkPartial = (
    request: IPartialChunkUploadRequest,
    fields?: EFieldGroup[]
  ): Promise<IPartialUploadStatus> =>
    this.api.patch(`/assets/upload/partial`, request, fields);
}

class Referrals {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getList = (fields?: EFieldGroup[]): Promise<IReferralRecord[]> =>
    this.api.get(`/referrals`, {}, fields);

  getChannels = (
    request: IGetPartnerChannelsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IPartnerChannel>> =>
    this.api.get(`/referrals/channels`, request, fields);

  addChannel = (
    request: IAddPartnerChannelRequest,
    fields?: EFieldGroup[]
  ): Promise<IPartnerChannel> =>
    this.api.post(`/referrals/channels`, request, fields);

  lookupPartnerChannel = (
    id: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.get(`/referrals/channels/lookup/${id}`, {}, fields);

  setChannelStatus = (
    pc: TIdentifier,
    status: EPartnerChannelStatus,
    fields?: EFieldGroup[]
  ): Promise<IPartnerChannel> =>
    this.api.patch(`/referrals/channels/${pc}/status/${status}`, {}, fields);

  deleteChannel = (pc: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.delete(`/referrals/channels/${pc}`, {}, fields);
}

class ContentPlan {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  get = (
    rc: TIdentifier,
    request: IGetContentPlanRequest,
    fields?: EFieldGroup[]
  ): Promise<IContentPlanVideo[]> =>
    this.api.get(`/reactrino/content-plan/${rc}`, request, fields);

  addVideo = (
    rc: TIdentifier,
    request: IAddContentPlanVideoRequest,
    fields?: EFieldGroup[]
  ): Promise<IContentPlanVideo> =>
    this.api.post(`/reactrino/content-plan/${rc}`, request, fields);

  getVideo = (
    video: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IContentPlanVideo> =>
    this.api.get(`/reactrino/content-plan/video/${video}`, {}, fields);

  updateVideo = (
    video: TIdentifier,
    request: IUpdateContentPlanVideoRequest,
    fields?: EFieldGroup[]
  ): Promise<IContentPlanVideo> =>
    this.api.patch(`/reactrino/content-plan/video/${video}`, request, fields);

  deleteVideo = (
    video: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/reactrino/content-plan/video/${video}`, {}, fields);
}

class Docs {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  index = (request: IGetDocsRequest, fields?: EFieldGroup[]): Promise<IDoc[]> =>
    this.api.get(`/docs`, request, fields);

  getPath = (doc: TIdentifier, fields?: EFieldGroup[]): Promise<IDocItem[]> =>
    this.api.get(`/docs/${doc}/path`, {}, fields);

  getPlainTree = (
    request: IGetPlainTreeRequest,
    fields?: EFieldGroup[]
  ): Promise<IDocPlainNode[]> =>
    this.api.get(`/docs/plain-tree`, request, fields);

  create = (
    request: ICreateDocRequest,
    fields?: EFieldGroup[]
  ): Promise<IDoc> => this.api.post(`/docs`, request, fields);

  sort = (request: ISortDocsRequest, fields?: EFieldGroup[]): Promise<IDoc[]> =>
    this.api.patch(`/docs/sort`, request, fields);

  update = (
    doc: TIdentifier,
    request: IUpdateDocRequest,
    fields?: EFieldGroup[]
  ): Promise<IDoc> => this.api.patch(`/docs/${doc}`, request, fields);

  getByTag = (tag: TIdentifier, fields?: EFieldGroup[]): Promise<IDoc> =>
    this.api.get(`/docs/tag/${tag}`, {}, fields);

  getById = (doc: TIdentifier, fields?: EFieldGroup[]): Promise<IDoc> =>
    this.api.get(`/docs/${doc}`, {}, fields);

  delete = (doc: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.delete(`/docs/${doc}`, {}, fields);
}

class ReviewTracks {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  proceedContentId = (
    request: IBulkTracksRequest,
    fields?: EFieldGroup[]
  ): Promise<number> =>
    this.api.post(`/review/tracks/proceed/content-id`, request, fields);

  proceedContentIdVideo = (
    fields?: EFieldGroup[]
  ): Promise<IGenerateReviewTrackVideoTask> =>
    this.api.post(`/review/tracks/proceed/content-id/video`, {}, fields);

  finishContentId = (
    request: IBulkTrackCommentRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.post(`/review/tracks/proceed/content-id/result`, request, fields);

  finishSpotify = (
    request: IBulkTrackCommentRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.post(`/review/tracks/proceed/spotify/result`, request, fields);

  setReviewStatus = (
    track: TIdentifier,
    request: ISetReviewStatusRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.patch(`/review/tracks/${track}/review-status`, request, fields);
}

class Main {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  checkVersion = (
    item: TIdentifier,
    version: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<{ version: string; upgrade: boolean }> =>
    this.api.get(`/version/${item}/${version}`, {}, fields);

  getServerHealth = (fields?: EFieldGroup[]): Promise<IHealth> =>
    this.api.get(`/health`, {}, fields);

  getAlbumsFeed = (fields?: EFieldGroup[]): Promise<IAlbum[]> =>
    this.api.post(`/albums-feed`, {}, fields);
}

class Tasks {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getTask = <T>(id: TIdentifier, fields?: EFieldGroup[]): Promise<T> =>
    this.api.get(`/tasks/${id}`, {}, fields);

  generateText = (
    request: IGenerateTextRequest,
    fields?: EFieldGroup[]
  ): Promise<IGenerateTextTask> =>
    this.api.post(`/tasks/text`, request, fields);

  generateVideoEmbedTexts = (
    request: IGenerateVideoEmbedTextsRequest,
    fields?: EFieldGroup[]
  ): Promise<IGenerateVideoEmbedTextsTask> =>
    this.api.post(`/tasks/video-embed-texts`, request, fields);

  createReviewAlbumsArchive = (
    fields?: EFieldGroup[]
  ): Promise<ICreateReviewAlbumsArchiveTask> =>
    this.api.post(`/tasks/create-review-albums-archive`, {}, fields);

  makeLyricsAnalysis = (
    track: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IMakeLyricsAnalysisTask> =>
    this.api.post(`/tasks/make-lyrics-analysis/${track}`, {}, fields);

  exportTracksCovers = (
    request: IBulkTracksRequest,
    fields?: EFieldGroup[]
  ): Promise<IExportTracksCoversTask> =>
    this.api.post(`/tasks/export-tracks-covers`, request, fields);

  exportTracksAudio = (
    request: IBulkTracksRequest,
    fields?: EFieldGroup[]
  ): Promise<IExportTracksAudioTask> =>
    this.api.post(`/tasks/export-tracks-audio`, request, fields);

  renderVideoEmbed = (
    ve: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IRenderVideoEmbedTask> =>
    this.api.post(`/tasks/render-video-embed/${ve}`, {}, fields);

  updateTask = <T>(
    id: TIdentifier,
    request: IUpdateTaskRequest,
    fields?: EFieldGroup[]
  ): Promise<T> => this.api.patch(`/tasks/${id}`, request, fields);

  customAiRequest = (
    request: ICustomAiRequest,
    fields?: EFieldGroup[]
  ): Promise<ICustomAiRequestTask> =>
    this.api.post(`/tasks/ai`, request, fields);
}

class Channels {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  lookupChannel = (
    query: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.get(`/channels/lookup/${query}`, {}, fields);
}

class ReactorChannels {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getChannels = (fields?: EFieldGroup[]): Promise<IReactorChannel[]> =>
    this.api.get(`/reactrino/channels`, {}, fields);

  addChannel = (
    request: IAddReactionChannelRequest,
    fields?: EFieldGroup[]
  ): Promise<IReactorChannel> =>
    this.api.post(`/reactrino/channels`, request, fields);

  getChannel = (
    rc: TIdentifier,
    request: IReactorChannel,
    fields?: EFieldGroup[]
  ): Promise<unknown> =>
    this.api.get(`/reactrino/channels/${rc}`, request, fields);

  lookupChannel = (
    id: TIdentifier,
    request: IReactorChannel,
    fields?: EFieldGroup[]
  ): Promise<unknown> =>
    this.api.get(`/reactrino/channels/lookup/${id}`, request, fields);

  updateChannel = (
    rc: TIdentifier,
    request: IUpdateReactionChannelRequest,
    fields?: EFieldGroup[]
  ): Promise<IReactorChannel> =>
    this.api.patch(`/reactrino/channels/${rc}`, request, fields);

  deleteChannel = (rc: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.delete(`/reactrino/channels/${rc}`, {}, fields);

  getTrending = (
    rc: TIdentifier,
    request: ICommonGetListRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<ITrendingItem>> =>
    this.api.get(`/reactrino/channels/${rc}/trending`, request, fields);

  getNewVideos = (
    rc: TIdentifier,
    request: ICommonGetListRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IMonitoringVideo>> =>
    this.api.get(`/reactrino/channels/${rc}/new-videos`, request, fields);

  getComments = (
    rc: TIdentifier,
    request: ICommonGetListRequest,
    fields?: EFieldGroup[]
  ): Promise<IGetReactorCommentsResponse> =>
    this.api.get(`/reactrino/channels/${rc}/comments`, request, fields);

  refreshComments = (
    rc: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.post(`/reactrino/channels/${rc}/comments/refresh`, {}, fields);

  deleteComment = (
    rc: TIdentifier,
    comment: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(
      `/reactrino/channels/${rc}/comments/${comment}`,
      {},
      fields
    );
}

class Reports {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getList = (fields?: EFieldGroup[]): Promise<IReport[]> =>
    this.api.get(`/reports`, {}, fields);

  addReport = (
    request: IAddReportRequest,
    fields?: EFieldGroup[]
  ): Promise<IReport> => this.api.post(`/reports`, request, fields);

  processReport = (
    report: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IProcessReportTask> =>
    this.api.post(`/reports/${report}/process`, {}, fields);

  payReport = (report: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.post(`/reports/${report}/pay`, {}, fields);

  getInfo = (
    fields?: EFieldGroup[]
  ): Promise<{ lastReport: string; nextReport: string }> =>
    this.api.get(`/reports/info`, {}, fields);
}

class AdminStats {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getTracksViewsHistory = (
    fields?: EFieldGroup[]
  ): Promise<{ date: string; views: number; views_inc: number }[]> =>
    this.api.get(`/admin/stats/ve`, {}, fields);
}

class Tracks {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getTracksList = (
    request: IGetTracksRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<ITrack>> => this.api.get(`/tracks`, request, fields);

  getRemainingVideoTracks = (fields?: EFieldGroup[]): Promise<ITrack[]> =>
    this.api.get(`/tracks/remaining-video`, {}, fields);

  addTrack = (
    request: IAddTrackRequest,
    fields?: EFieldGroup[]
  ): Promise<ITrack> => this.api.post(`/tracks`, request, fields);

  updateTrack = (
    track: TIdentifier,
    request: IUpdateTrackRequest,
    fields?: EFieldGroup[]
  ): Promise<ITrack> => this.api.patch(`/tracks/${track}`, request, fields);

  updateTrackMonetization = (
    track: TIdentifier,
    request: IUpdateMonetizationRequest,
    fields?: EFieldGroup[]
  ): Promise<ITrack> =>
    this.api.patch(`/tracks/${track}/monetization`, request, fields);

  getLastPerformerTrack = (
    performer: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<ITrack | null> =>
    this.api.get(`/tracks/last/${performer}`, {}, fields);

  getTrack = (track: TIdentifier, fields?: EFieldGroup[]): Promise<ITrack> =>
    this.api.get(`/tracks/${track}`, {}, fields);

  getTrackInfo = (
    track: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<ITrackInfo> => this.api.get(`/tracks/${track}/info`, {}, fields);

  deleteTrack = (track: TIdentifier, fields?: EFieldGroup[]): Promise<true> =>
    this.api.delete(`/tracks/${track}`, {}, fields);

  adminDraftTrack = (
    track: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<true> =>
    this.api.patch(`/tracks/${track}/admin-draft`, {}, fields);

  adminReviewTrack = (
    track: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<true> =>
    this.api.patch(`/tracks/${track}/admin-review`, {}, fields);

  draftTrack = (track: TIdentifier, fields?: EFieldGroup[]): Promise<true> =>
    this.api.patch(`/tracks/${track}/draft`, {}, fields);

  rejectTrack = (
    track: TIdentifier,
    request: ICommonRejectRequest,
    fields?: EFieldGroup[]
  ): Promise<true> =>
    this.api.patch(`/tracks/${track}/reject`, request, fields);

  approveTrack = (track: TIdentifier, fields?: EFieldGroup[]): Promise<true> =>
    this.api.patch(`/tracks/${track}/approve`, {}, fields);
}

class Performers {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getList = (
    request: IGetPerformersListRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IPerformer>> =>
    this.api.get(`/performers`, request, fields);

  createPerformer = (
    request: ICreatePerformerRequest,
    fields?: EFieldGroup[]
  ): Promise<IPerformer> => this.api.post(`/performers`, request, fields);

  getPerformer = (
    performer: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IPerformer> =>
    this.api.get(`/performers/${performer}`, {}, fields);

  updatePerformer = (
    performer: TIdentifier,
    request: IUpdatePerformerRequest,
    fields?: EFieldGroup[]
  ): Promise<IPerformer> =>
    this.api.patch(`/performers/${performer}`, request, fields);

  reviewPerformer = (
    performer: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IPerformer> =>
    this.api.post(`/performers/${performer}/review`, {}, fields);

  draftPerformer = (
    performer: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IPerformer> =>
    this.api.post(`/performers/${performer}/draft`, {}, fields);

  approvePerformer = (
    performer: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IPerformer> =>
    this.api.post(`/performers/${performer}/approve`, {}, fields);

  rejectPerformer = (
    performer: TIdentifier,
    request: ICommonRejectRequest,
    fields?: EFieldGroup[]
  ): Promise<IPerformer> =>
    this.api.post(`/performers/${performer}/reject`, request, fields);

  deletePerformer = (
    performer: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/performers/${performer}`, {}, fields);
}

class Notifications {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getList = (fields?: EFieldGroup[]): Promise<INotification[]> =>
    this.api.get(`/notifications`, {}, fields);

  read = (
    notification: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<INotification> =>
    this.api.patch(`/notifications/${notification}`, {}, fields);

  readAll = (fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.patch(`/notifications`, {}, fields);

  delete = (
    notification: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/notifications/${notification}`, {}, fields);

  deleteAll = (fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.delete(`/notifications`, {}, fields);
}

class Albums {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getAlbums = (
    request: IGetAlbumsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IAlbum>> => this.api.get(`/albums`, request, fields);

  createAlbum = (
    request: ICreateAlbumRequest,
    fields?: EFieldGroup[]
  ): Promise<IAlbum> => this.api.post(`/albums`, request, fields);

  createSingle = (
    request: ICreateSingleRequest,
    fields?: EFieldGroup[]
  ): Promise<IAlbum> => this.api.post(`/albums/single`, request, fields);

  getAlbum = (album: TIdentifier, fields?: EFieldGroup[]): Promise<IAlbum> =>
    this.api.get(`/albums/${album}`, {}, fields);

  lockAlbum = (album: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.patch(`/albums/${album}/lock`, {}, fields);

  unlockAlbum = (
    album: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.patch(`/albums/${album}/unlock`, {}, fields);

  updateAlbum = (
    album: TIdentifier,
    request: IUpdateAlbumRequest,
    fields?: EFieldGroup[]
  ): Promise<IAlbum> => this.api.patch(`/albums/${album}`, request, fields);

  updateAlbumLinks = (
    album: TIdentifier,
    request: IUpdateAlbumLinksRequest,
    fields?: EFieldGroup[]
  ): Promise<IAlbumLinks> =>
    this.api.patch(`/albums/${album}/links`, request, fields);

  draftAlbum = (album: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.post(`/albums/${album}/draft`, {}, fields);

  reviewAlbum = (
    album: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.post(`/albums/${album}/review`, {}, fields);

  approveAlbum = (
    album: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.post(`/albums/${album}/approve`, {}, fields);

  rejectAlbum = (
    album: TIdentifier,
    request: ICommonRejectRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.post(`/albums/${album}/reject`, request, fields);

  deleteAlbum = (
    album: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.delete(`/albums/${album}`, {}, fields);
}

class Monitoring {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getCategories = (fields?: EFieldGroup[]): Promise<IMonitoringCategory[]> =>
    this.api.get(`/monitoring/categories`, {}, fields);

  createCategory = (
    request: ICreateMonitoringCategoryRequest,
    fields?: EFieldGroup[]
  ): Promise<IMonitoringCategory> =>
    this.api.post(`/monitoring/categories`, request, fields);

  updateCategory = (
    cat: TIdentifier,
    request: ICreateMonitoringCategoryRequest,
    fields?: EFieldGroup[]
  ): Promise<IMonitoringCategory> =>
    this.api.patch(`/monitoring/categories/${cat}`, request, fields);

  deleteCategory = (
    cat: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/monitoring/categories/${cat}`, {}, fields);

  getChannels = (
    request: IGetMonitoringChannelsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IMonitoringChannel>> =>
    this.api.get(`/monitoring/channels`, request, fields);

  createChannel = (
    request: ICreateMonitoringChannelRequest,
    fields?: EFieldGroup[]
  ): Promise<IMonitoringChannel> =>
    this.api.post(`/monitoring/channels`, request, fields);

  getChannel = (
    channel: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IMonitoringChannel> =>
    this.api.get(`/monitoring/channels/${channel}`, {}, fields);

  deleteChannel = (
    channel: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/monitoring/channels/${channel}`, {}, fields);

  getSubscriptions = (fields?: EFieldGroup[]): Promise<IMonitoringCategory[]> =>
    this.api.get(`/monitoring/subscriptions`, {}, fields);

  subscribe = (
    category: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.post(`/monitoring/subscriptions/${category}`, {}, fields);

  unsubscribe = (
    category: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> =>
    this.api.delete(`/monitoring/subscriptions/${category}`, {}, fields);
}

class Applications {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  submitPerformerApplication = (
    request: IPerformerApplicationSubmitRequest,
    fields?: EFieldGroup[]
  ): Promise<number> =>
    this.api.post(`/applications/performer`, request, fields);

  getPerformerApplications = (
    request: IGetApplicationsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IPerformerApplication>> =>
    this.api.get(`/applications/performer`, request, fields);

  rejectPerformerApplication = (
    application: TIdentifier,
    request: ICommonRejectRequest,
    fields?: EFieldGroup[]
  ): Promise<IPerformerApplication> =>
    this.api.post(
      `/applications/performer/${application}/reject`,
      request,
      fields
    );

  becomePerformerManager = (
    application: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IPerformerApplication> =>
    this.api.post(`/applications/performer/${application}/manager`, {}, fields);

  becomeYoutuberManager = (
    application: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IPerformerApplication> =>
    this.api.post(`/applications/youtuber/${application}/manager`, {}, fields);

  updatePerformerApplication = (
    application: TIdentifier,
    request: IUpdateApplicationRequest,
    fields?: EFieldGroup[]
  ): Promise<IPerformerApplication> =>
    this.api.patch(`/applications/performer/${application}`, request, fields);

  approvePerformerApplication = (
    application: TIdentifier,
    request: IApproveApplicationRequest,
    fields?: EFieldGroup[]
  ): Promise<IPerformerApplication> =>
    this.api.post(
      `/applications/performer/${application}/approve`,
      request,
      fields
    );

  submitYoutuberApplication = (
    request: IYoutuberApplicationSubmitRequest,
    fields?: EFieldGroup[]
  ): Promise<number> =>
    this.api.post(`/applications/youtuber`, request, fields);

  getYoutuberApplications = (
    request: IGetApplicationsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IYoutuberApplication>> =>
    this.api.get(`/applications/youtuber`, request, fields);

  rejectYoutuberApplication = (
    application: TIdentifier,
    request: ICommonRejectRequest,
    fields?: EFieldGroup[]
  ): Promise<IYoutuberApplication> =>
    this.api.post(
      `/applications/youtuber/${application}/reject`,
      request,
      fields
    );

  approveYoutuberApplication = (
    application: TIdentifier,
    request: IApproveApplicationRequest,
    fields?: EFieldGroup[]
  ): Promise<IYoutuberApplication> =>
    this.api.post(
      `/applications/youtuber/${application}/approve`,
      request,
      fields
    );
}

class Attachments {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  createAttachment = (
    request: ICreateAttachmentRequest,
    fields?: EFieldGroup[]
  ): Promise<IAttachment> => this.api.post(`/attachments`, request, fields);

  getAttachment = (
    att: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IAttachment> => this.api.get(`/attachments/${att}`, {}, fields);

  updateAttachment = (
    att: TIdentifier,
    request: IUpdateAttachmentRequest,
    fields?: EFieldGroup[]
  ): Promise<IAttachment> =>
    this.api.patch(`/attachments/${att}`, request, fields);

  deleteAttachment = (
    att: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.delete(`/attachments/${att}`, {}, fields);
}

class Storage {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getItem = <T>(
    request: IGetStorageItemRequest,
    fields?: EFieldGroup[]
  ): Promise<T | null> => this.api.get(`/storage`, request, fields);

  setItem = (
    request: ISetStorageItemRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.put(`/storage`, request, fields);

  deleteItem = (
    request: IDeleteStorageItemRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.delete(`/storage`, request, fields);
}

class Utils {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  isMonetized = (
    handle: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.get(`/utils/monetize/${handle}`, {}, fields);

  sendAnalytics = (
    request: ISendAnalyticsRequest,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.post(`/utils/analytics`, request, fields);
}

class Companies {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getList = (fields?: EFieldGroup[]): Promise<ICompany[]> =>
    this.api.get(`/companies`, {}, fields);

  createCompany = (
    request: IUpdateCompanyRequest,
    fields?: EFieldGroup[]
  ): Promise<ICompany> => this.api.post(`/companies`, request, fields);

  updateCompany = (
    company: TIdentifier,
    request: IUpdateCompanyRequest,
    fields?: EFieldGroup[]
  ): Promise<ICompany> =>
    this.api.patch(`/companies/${company}`, request, fields);

  getCompany = (
    company: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<ICompany> => this.api.get(`/companies/${company}`, {}, fields);

  deleteCompany = (
    company: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<boolean> => this.api.delete(`/companies/${company}`, {}, fields);
}

class Reactrino {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getTrending = (
    country: TIdentifier,
    request: ICommonGetListRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<ITrendingItem>> =>
    this.api.post(`/reactrino/trending/${country}`, request, fields);

  getNewVideos = (
    category: TIdentifier,
    request: ICommonGetListRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<IMonitoringVideo>> =>
    this.api.post(`/reactrino/new-videos/${category}`, request, fields);

  getMonetizationStatus = (
    fields?: EFieldGroup[]
  ): Promise<IYoutuberApplication | null> =>
    this.api.get(`/reactrino/monetization`, {}, fields);

  submitApplication = (
    request: ISubmitMonetizationApplicationRequest,
    fields?: EFieldGroup[]
  ): Promise<IYoutuberApplication> =>
    this.api.post(`/reactrino/monetization`, request, fields);
}

class Genres {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getGenres = (fields?: EFieldGroup[]): Promise<IGenre[]> =>
    this.api.get(`/genres`, {}, fields);

  getSubgenres = (
    genre: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<IGenre[]> =>
    this.api.get(`/genres/${genre}/subgenres`, {}, fields);

  getItem = (genre: TIdentifier, fields?: EFieldGroup[]): Promise<IGenre> =>
    this.api.get(`/genres/${genre}`, {}, fields);
}

class Library {
  private api: RestAPI;
  constructor(api: RestAPI) {
    this.api = api;
  }

  getItemsList = (
    request: IGetLibraryItemsRequest,
    fields?: EFieldGroup[]
  ): Promise<IPagedData<ILibraryItem>> =>
    this.api.get(`/library`, request, fields);

  createItem = (
    request: ICreateLibraryItemRequest,
    fields?: EFieldGroup[]
  ): Promise<ILibraryItem> => this.api.post(`/library`, request, fields);

  getItem = (
    item: TIdentifier,
    fields?: EFieldGroup[]
  ): Promise<ILibraryItem> => this.api.get(`/library/${item}`, {}, fields);

  deleteItem = (item: TIdentifier, fields?: EFieldGroup[]): Promise<boolean> =>
    this.api.delete(`/library/${item}`, {}, fields);
}
